import { useRef, useState } from "react";
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, FileText, Loader2, Upload, X } from "lucide-react";
import { AwpRuleSet, CatalogueRead, RULE_LAYERS, RuleLayer, importRuleSet, readCatalogue } from "./rulesApi";

interface ImportCatalogueModalProps {
  /** A file dropped on the library's drop zone, so the dialog opens with it already chosen. */
  initialFile?: File | null;
  onCancel: () => void;
  onImported: (set: AwpRuleSet) => void;
}

type Phase = "choose" | "reading" | "verify";

/**
 * Upload an AWP standard — the same two steps, and the same window, as an engineering upload.
 *
 * CHOOSE the Word document and press Next: it is read and nothing is stored in the library. VERIFY
 * shows what was read — rules per domain, checked against the document's own totals — with the
 * title, number, version and layer to confirm. Add to Library writes it as a Draft rule set.
 */
const ImportCatalogueModal = ({ initialFile = null, onCancel, onImported }: ImportCatalogueModalProps) => {
  const filePicker = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(initialFile);
  const [phase, setPhase] = useState<Phase>("choose");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [read, setRead] = useState<CatalogueRead | null>(null);
  const [title, setTitle] = useState("");
  const [docNo, setDocNo] = useState("");
  const [version, setVersion] = useState("");
  const [layer, setLayer] = useState<RuleLayer>("technical");

  const readDocument = async () => {
    if (!file) return;
    setPhase("reading");
    setError(null);
    try {
      const result = await readCatalogue(file);
      setRead(result);
      setTitle(result.metadata.title);
      setDocNo(result.metadata.docNo);
      setVersion(result.metadata.version);
      setPhase("verify");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read the document.");
      setPhase("choose");
    }
  };

  const submit = async () => {
    if (!read || !title.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const set = await importRuleSet({
        source: read.source,
        title: title.trim(),
        docNo: docNo.trim() || undefined,
        version: version.trim() || undefined,
        layer,
      });
      onImported(set);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add the standard.");
      setSaving(false);
    }
  };

  // A title already in the library is the one thing that stops a repeat upload; the button says so.
  const titleTaken =
    !!read &&
    (read.takenTitles ?? []).some((t) => t.trim().toLowerCase() === title.trim().toLowerCase());
  const blocked = !read || !title.trim() || titleTaken || read.ruleCount === 0;

  return (
    <div className="vr-backdrop" role="dialog" aria-modal="true" aria-label="Upload standard">
      <div className="vr-modal">
        {saving && (
          <div className="vr-busy">
            <div className="vr-busy-card">
              <Loader2 size={22} className="vr-spin-inline" />
              <p className="vr-busy-title">Adding to the library</p>
              <p className="vr-busy-sub">Writing {read?.ruleCount ?? 0} rules. This takes a few seconds.</p>
            </div>
          </div>
        )}

        <div className="vr-modal-head">
          <div>
            <h3>Upload Standard</h3>
          </div>
          <button className="vr-iconbtn" onClick={onCancel} aria-label="Close" disabled={saving}>
            <X size={16} />
          </button>
        </div>

        <div className="vr-modal-body">
          {error && <div className="vr-form-error">{error}</div>}

          {/* ── Step 1: choose the document, then read it ── */}
          {phase !== "verify" && (
            <div className="vr-field">
              <label>Source document</label>

              {file && (
                <div className="vr-file-list">
                  <div className="vr-file-row">
                    <FileText size={15} style={{ color: "hsl(var(--vr-primary))" }} />
                    <span className="vr-file-name">{file.name}</span>
                    <span className="vr-cell-dim">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                    {phase === "choose" && (
                      <button className="vr-rowbtn vr-danger" onClick={() => setFile(null)}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              )}

              {phase === "reading" ? (
                <div className="vr-reading">
                  <Loader2 size={16} className="vr-spin-inline" />
                  <span>Reading {file?.name}…</span>
                </div>
              ) : (
                !file && (
                  <>
                    <input
                      ref={filePicker}
                      type="file"
                      accept=".pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const picked = e.target.files?.[0];
                        if (picked) setFile(picked);
                        e.target.value = "";
                      }}
                    />
                    <button
                      type="button"
                      className="vr-btn vr-btn-outline"
                      style={{ alignSelf: "flex-start" }}
                      onClick={() => filePicker.current?.click()}
                    >
                      <FileText size={14} /> Choose File
                    </button>
                    <span className="vr-field-hint">
                      PDF, Word or Excel. Word rule catalogues are read today; PDF and Excel are next.
                    </span>
                  </>
                )
              )}
            </div>
          )}

          {/* ── Step 2: confirm what was read ── */}
          {phase === "verify" && read && (
            <>
              <div className="vr-file-list">
                <div className="vr-file-row">
                  <FileText size={15} style={{ color: "hsl(var(--vr-primary))" }} />
                  <span className="vr-file-name">{read.source.fileName}</span>
                </div>
              </div>

              {/* A note, not a stop: the same document can go in again under a title of its own. */}
              {read.duplicateOf && (
                <div className="vr-awp-readout vr-awp-readout-warn">
                  <AlertTriangle size={15} />
                  <span>
                    This document is already in the library as “{read.duplicateOf.title}”. Give this copy a
                    different title to add it again.
                  </span>
                </div>
              )}

              <div className={`vr-awp-readout${read.problems.length ? " vr-awp-readout-warn" : ""}`}>
                {read.problems.length === 0 ? (
                  <>
                    <CheckCircle2 size={15} />
                    <span>
                      {read.ruleCount} rules read across {read.domains.length} domains
                      {read.metadata.declaredRuleCount !== undefined
                        ? ` — matches the ${read.metadata.declaredRuleCount} the document states.`
                        : "."}
                    </span>
                  </>
                ) : (
                  <>
                    <AlertTriangle size={15} />
                    <span>
                      {read.ruleCount} rules read, but {read.problems.length}{" "}
                      {read.problems.length === 1 ? "thing does" : "things do"} not add up:
                    </span>
                  </>
                )}
              </div>
              {read.problems.length > 0 && (
                <ul className="vr-awp-problems">
                  {read.problems.slice(0, 12).map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                  {read.problems.length > 12 && <li>…and {read.problems.length - 12} more.</li>}
                </ul>
              )}

              <div className="vr-awp-domains">
                {read.domains.map((d) => (
                  <div key={d.code} className="vr-awp-domain">
                    <span className="vr-cell-code">{d.code}</span>
                    <span>{d.name}</span>
                    <span className={d.declared !== undefined && d.declared !== d.rules ? "vr-warn" : "vr-cell-dim"}>
                      {d.rules}
                    </span>
                  </div>
                ))}
              </div>

              <div className="vr-field">
                <label htmlFor="vr-rs-title">Title</label>
                <input id="vr-rs-title" className="vr-input" value={title} onChange={(e) => setTitle(e.target.value)} />
                {titleTaken && (
                  <span className="vr-field-hint vr-warn">A standard with this title is already in the library.</span>
                )}
              </div>
              <div className="vr-field-grid">
                <div className="vr-field">
                  <label htmlFor="vr-rs-no">Document No.</label>
                  <input id="vr-rs-no" className="vr-input" value={docNo} onChange={(e) => setDocNo(e.target.value)} />
                </div>
                <div className="vr-field">
                  <label htmlFor="vr-rs-ver">Version</label>
                  <input id="vr-rs-ver" className="vr-input" value={version} onChange={(e) => setVersion(e.target.value)} />
                </div>
              </div>
              <div className="vr-field">
                <label htmlFor="vr-rs-layer">Layer</label>
                <select
                  id="vr-rs-layer"
                  className="vr-select"
                  value={layer}
                  onChange={(e) => setLayer(e.target.value as RuleLayer)}
                >
                  {RULE_LAYERS.map((l) => (
                    <option key={l.key} value={l.key}>
                      {l.label}
                    </option>
                  ))}
                </select>
                <span className="vr-field-hint">
                  Where two standards disagree, the higher layer wins: Contract → Project → Company →
                  Technical Standards → Industry Baseline.
                </span>
              </div>

              {read.sample.length > 0 && (
                <div className="vr-field">
                  <label>First rules, as read</label>
                  <div className="vr-awp-sample">
                    {read.sample.map((r) => (
                      <div key={r.code} className="vr-awp-sample-row">
                        <strong>{r.code}</strong> {r.title}
                        <div className="vr-cell-code">
                          {r.method} · {r.severity} · {r.gate}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="vr-modal-foot">
          {phase === "verify" && (
            <button
              className="vr-btn vr-btn-ghost"
              onClick={() => {
                setPhase("choose");
                setRead(null);
                setError(null);
              }}
              disabled={saving}
              title="Go back and choose a different document"
            >
              <ArrowLeft size={14} /> Back
            </button>
          )}

          <button className="vr-btn vr-btn-ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>

          {phase !== "verify" ? (
            <button
              className="vr-btn vr-btn-glow"
              onClick={() => void readDocument()}
              disabled={!file || phase === "reading"}
              title={!file ? "Choose a document first" : undefined}
            >
              {phase === "reading" ? (
                <>
                  <Loader2 size={14} className="vr-spin-inline" /> Reading…
                </>
              ) : (
                <>
                  Next <ArrowRight size={14} />
                </>
              )}
            </button>
          ) : (
            <button className="vr-btn vr-btn-glow" onClick={() => void submit()} disabled={blocked || saving}>
              {saving ? (
                <>
                  <Loader2 size={14} className="vr-spin-inline" /> Adding…
                </>
              ) : (
                <>
                  <Upload size={14} /> Add to Library
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportCatalogueModal;
